import {
  Component,
  signal,
  PLATFORM_ID,
  Inject,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { Title, Meta } from '@angular/platform-browser';

import { ConfirmInformationComponent } from '../confirm-information/confirm-information.component';
import { LoginComponent } from '../login/login.component';
import { ToAccessComponent } from '../to-access/to-access.component';
import { FinishedComponent } from '../finished/finished.component';
import { AccountDetectedComponent } from '../account-detected/account-detected.component';
import {
  LoginState,
  LoginStateType,
  LoginStateObject,
  AccountDetectedState,
  PhoneVerificationState,
} from '../../../types/login.types';
import { LoginService } from '../../../services/login.service';
import { GoBackBtnComponent } from '../shared/go-back-btn/go-back-btn.component';
import { PhoneVerificationComponent } from '../phone-verification/phone-verification.component';

@Component({
  selector: 'app-main-login',
  imports: [
    CommonModule,
    FormsModule,
    LoginComponent,
    ConfirmInformationComponent,
    ToAccessComponent,
    FinishedComponent,
    AccountDetectedComponent,
    LucideAngularModule,
    GoBackBtnComponent,
    PhoneVerificationComponent,
  ],
  templateUrl: './main-login.component.html',
})
export class MainLoginComponent implements OnInit {
  private title = inject(Title);
  private meta = inject(Meta);

  isFirstLoad = signal<boolean>(true);

  currentLoginState = signal<LoginStateObject>({
    type: LoginState.login,
    timestamp: new Date(),
  });
  LoginState = LoginState;

  constructor(
    private readonly router: Router,
    private readonly loginService: LoginService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.loginService.loginState$.subscribe((state) => {
      if (this.isFirstLoad()) {
        this.isFirstLoad.set(false);
        return;
      }

      this.currentLoginState.set(state);
    });
  }

  ngOnInit() {
    this.setMetaTags();
  }

  private setMetaTags() {
    const pageTitle = 'Iniciar Sesión | Allas Repuestos';
    const description =
      'Inicia sesión en Allas Repuestos para acceder a tu cuenta. Comprá repuestos automotrices con envío a domicilio en Honduras.';
    const imageUrl = 'https://www.allasrepuestos.com/logos/isotipo.webp';

    this.title.setTitle(pageTitle);

    this.meta.updateTag({
      name: 'description',
      content: description,
    });

    this.meta.updateTag({
      property: 'og:title',
      content: pageTitle,
    });

    this.meta.updateTag({
      property: 'og:description',
      content: description,
    });

    this.meta.updateTag({
      property: 'og:type',
      content: 'website',
    });

    this.meta.updateTag({
      property: 'og:image',
      content: imageUrl,
    });

    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });

    this.meta.updateTag({
      name: 'twitter:title',
      content: pageTitle,
    });

    this.meta.updateTag({
      name: 'twitter:description',
      content: description,
    });
  }

  goBack(): void {
    switch (this.currentLoginState().type) {
      case LoginState.login:
        this.router.navigate(['/']);
        break;
      case LoginState.confirmInformation:
        this.loginService.notifyLoginState({
          type: LoginState.login,
          timestamp: new Date(),
        });
        break;
      case LoginState.accountDetected:
        const accountDetectedState =
          this.currentLoginState() as AccountDetectedState;
        this.loginService.notifyLoginState({
          type: LoginState.phoneVerification,
          timestamp: new Date(),
          loginFlow: {
            name: accountDetectedState.loginFlow.name,
            email: accountDetectedState.loginFlow.email,
            idProveedor: accountDetectedState.loginFlow.idProveedor,
            idToken: accountDetectedState.loginFlow.idToken,
            password: accountDetectedState.loginFlow.password,
            redirectUrl: accountDetectedState.loginFlow.redirectUrl,
          },
        });
        break;
      case LoginState.toAccess:
        this.loginService.notifyLoginState({
          type: LoginState.login,
          timestamp: new Date(),
        });
        break;
      case LoginState.phoneVerification:
        const phoneVerificationState =
          this.currentLoginState() as PhoneVerificationState;
        this.loginService.notifyLoginState({
          type: LoginState.confirmInformation,
          timestamp: new Date(),
          loginFlow: {
            name: phoneVerificationState.loginFlow.name,
            email: phoneVerificationState.loginFlow.email,
            idProveedor: phoneVerificationState.loginFlow.idProveedor,
            idToken: phoneVerificationState.loginFlow.idToken,
            redirectUrl: phoneVerificationState.loginFlow.redirectUrl,
          },
        });
        break;
      default:
        this.router.navigate(['/']);
    }
  }

  // Helper to get current state type for template compatibility
  get currentStateType(): LoginStateType {
    return this.currentLoginState().type;
  }

  reloadPage(): void {
    this.router.navigate(['/']);
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }
}
